<template>
  <div class="register-bonuses">
    <div class="register-bonuses__header">
      <UiText
        class="register-bonuses__heading"
        tag="h2"
        font="body-h2"
        color="typography-title01"
        align-center
        v-html="$t('regAuthForgot.regBonus.heading', {br: '<br />'})"
      />
    </div>
    <Transition name="fade">
      <UiAlert
        v-if="isShowAlertPromocodeActivated"
        class="register-bonuses__alert"
        type="success"
        :icon="'common/check-circle'"
        :text="$tc('regAuthForgot.regBonus.promocodeActivated')"
        is-close
        @close="isShowAlertPromocodeActivated = false"
      />
    </Transition>
    <template v-if="isSliderInitiated">
      <div class="register-bonuses__slider">
        <div>
          <div v-swiper:SwiperInstance="swiperOptions" class="swiper">
            <div class="swiper-pagination" />
            <div class="swiper-wrapper">
              <div
                v-for="(slide, index) in normalizedRegisterBonusesWithPromocodeCard"
                :key="`${slide.code}${index}`"
                :data-id="`register-slide-${index}`"
                class="swiper-slide register-bonuses__card"
              >
                <img class="register-bonuses__img" :src="slide.imgSrc" alt="bonus" />
              </div>
            </div>
            <RegistrationBonusSliderButtons class="register-bonuses__buttons" />
            <RegistrationBonusSliderFog v-if="selectedBonus" :selected-bonus="selectedBonus" />
          </div>
        </div>
      </div>
      <div v-if="selectedBonus" class="register-bonuses__footer">
        <Transition name="fade" mode="out-in">
          <KeepAlive>
            <PromoCodeCard
              v-if="selectedBonus.isPromocode"
              ref="PromoCodeCardRef"
              @success-register-bonus="onPromocodeSuccess"
              @success-no-deb-bonus="onNoDepBonusSuccess"
            />
            <PreviewCard
              v-else
              :key="`${selectedBonus.code}${selectedBonus.index}`"
              :bonus="selectedBonus"
              @to-cashbox="toCashboxBonus"
            />
          </KeepAlive>
        </Transition>
      </div>
    </template>
    <RegistrationBonusSkeleton v-else />
    <RegistrationBonusModalNoBonusWarn ref="RegistrationBonusModalNoBonusWarnRef" />
    <RegistrationBonusModalNoDepPromoCodeInfo ref="RegistrationBonusModalNoDepPromoCodeInfoRef" />
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import RegistrationBonusSkeleton from '~/components/BigModal/RegAuthForgot/RegistrationBonuses/Skeleton'
import { GTM_REG_BONUS } from '~/constants/gtm'
import nuxtEmits from '~/constants/nuxtEmits'
import { formatNumberFilter } from '~/util/helpers/vueFilters'
/** components */
import RegistrationBonusModalNoBonusWarn from '~/components/BigModal/RegAuthForgot/RegistrationBonuses/Modal/NoBonusWarn'
import RegistrationBonusModalNoDepPromoCodeInfo from '~/components/BigModal/RegAuthForgot/RegistrationBonuses/Modal/NoDepPromoCodeInfo'
import PromoCodeCard from '~/components/BigModal/RegAuthForgot/RegistrationBonuses/PromoCodeCard'
import PreviewCard from '~/components/BigModal/RegAuthForgot/RegistrationBonuses/PreviewCard'
import RegistrationBonusSliderFog from '~/components/BigModal/RegAuthForgot/RegistrationBonuses/SliderFog'
import RegistrationBonusSliderButtons from '~/components/BigModal/RegAuthForgot/RegistrationBonuses/SliderButtons'

import { Swiper, Navigation, Pagination } from 'swiper'
Swiper.use([Navigation, Pagination])

export const BONUS_TYPE = {
  WELCOME_PACK: 'welcomePack',
  WELCOME_PACK_VIP: 'welcomePackVip',
  NO_BONUS: 'noBonus',
  PROMO_CODE: 'promocode',
}

let timeoutIdSuccessPromoCode
const SHOW_ALERT_MS = 5000
const INITIAL_ACTIVE_SLIDE_INDEX = 0

export default {
  name: 'RegistrationBonuses',
  components: {
    RegistrationBonusSkeleton,
    RegistrationBonusModalNoDepPromoCodeInfo, RegistrationBonusModalNoBonusWarn,
    PromoCodeCard, PreviewCard, RegistrationBonusSliderFog, RegistrationBonusSliderButtons,
  },
  static: { GTM_REG_BONUS },
  data() {
    return {
      selectedBonus: null,
      isShowAlertPromocodeActivated: false,
      isSliderInitiated: false,
    }
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    ...mapGetters('registerBonus', [
      'get_registerBonuses',
      'get_promocodeBonuses',
      'get_registerBonusesTotalCount',
      'getData_registerBonuses',
      'get_activatedBonusesCodesFromRegister',
      'get_welcomePackBonusesData',
    ]),
    swiperOptions() {
      return {
        /** Mobile */
        slidesPerView: 'auto',
        slideToClickedSlide: true,
        centeredSlides: true,
        watchOverflow: true,
        resizeObserver: false,
        preventClicks: true,
        preventClicksPropagation: false,
        threshold: 12,
        navigation: {
          nextEl: '.register-bonuses__next',
          prevEl: '.register-bonuses__prev',
        },
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          dynamicBullets: true,
          clickable: true,
          dynamicMainBullets: 1,
          renderBullet: (index, className) => {
            let text
            if (index === this.vipIndex) text = 'VIP'
            else if (index === this.promocodeIndex) text = 'Промо'
            else text = index + 1
            return `<span class="${className}" data-gtm-id="${GTM_REG_BONUS.ID.ga_deposite_click_btn_pagination}">${text}</span>`
          },
        },
        breakpoints: {
          768: {
            /** Desktop */
            shortSwipes: false,
            centeredSlides: true,
          },
        },
      }
    },
    registerBonusesWithPromocodeCard() {
      return [...this.get_registerBonuses, { isPromocode: true, icon_url: 'dummy_for_key' }]
    },
    normalizedRegisterBonusesWithPromocodeCard() {
      return this.registerBonusesWithPromocodeCard.map((bonus, index) => {
        const locale = this.$i18n.locale
        const isDesktop = this.$device.isDesktop
        const cloudUrl = this.$config.cloudUrl

        let imgSrc = ''

        const hasLocalizedImage = bonus?.[locale]?.image?.[isDesktop ? 'desktop' : 'mobile']
        const fallbackLocalizedImage = hasLocalizedImage ? cloudUrl + hasLocalizedImage : null

        if (bonus.isVip || bonus.isPromocodeBonus) {
          imgSrc = fallbackLocalizedImage || ''
        }
        else if (bonus.isPromocode) {
          imgSrc = '/img/welcome-pack/promocode.webp'
        }
        else if (bonus.icon_url) {
          imgSrc = cloudUrl + bonus.icon_url
        }
        else if (fallbackLocalizedImage) {
          imgSrc = fallbackLocalizedImage
        }

        const isWelcomePack = bonus.bonusType === BONUS_TYPE.WELCOME_PACK
        const code = isWelcomePack ? this.get_welcomePackBonusesData.code : bonus.code

        let textBonus = ''
        if (bonus.isPromoCode || bonus.isVip) {
          textBonus = bonus?.[locale]?.title || ''
        }
        else {
          textBonus = bonus?.[`name_${locale}`] || ''
        }

        const textMinDep = `${this.$t('regAuthForgot.regBonus.forMinDep')} ${formatNumberFilter(bonus.min_deposit)}`

        return {
          ...bonus,
          imgSrc,
          index,
          isWelcomePack,
          isPromocode: !!bonus.isPromocode,
          isTop: !!bonus.isTop,
          isVip: !!bonus.isVip,
          isPromocodeBonus: !!bonus.is_promocode || !!bonus.isPromocodeBonus,
          code,
          textBonus,
          textMinDep,
        }
      })
    },
    vipIndex() {
      return this.normalizedRegisterBonusesWithPromocodeCard.findIndex(b => b.isVip)
    },
    promocodeIndex() {
      return this.normalizedRegisterBonusesWithPromocodeCard.findIndex(b => b.isPromocode)
    },
  },
  async mounted() {
    if (!this.isLoggedIn) {
      this.$newModal.openRegistration({ noOldQuery: true })
      return
    }
    await this.fetch_registerBonuses()
    if (this.getData_registerBonuses.error || !this.get_registerBonusesTotalCount) {
      return await this.$router.replace({ path: this.$pathObj.cashbox.path })
    }
    this.initSlider()
    this.initAlertFirstLaunch()
    this.$gtm.push({ event: GTM_REG_BONUS.EVENT.ga_deposit_popup_show })
    this.initCloseModalMiddleware()
  },
  beforeDestroy() {
    clearTimeout(timeoutIdSuccessPromoCode)
    this.actReset_activatedBonusesByPromoCode()
  },
  methods: {
    ...mapActions('registerBonus', {
      fetch_registerBonuses: 'fetch_registerBonuses',
      act_activeBonus: 'activeBonus',
      actReset_activatedBonusesByPromoCode: 'actReset_activatedBonusesByPromoCode',
    }),
    ...mapActions('bonuses', ['transfer_depositBonus']),
    initCloseModalMiddleware() {
      this.$nuxt.$on(nuxtEmits.closeRegBonus, async () => {
        const userAnswer = await this.$refs?.RegistrationBonusModalNoBonusWarnRef?.$asyncOpen()
        if (userAnswer) this.toCashboxNoBonus()
      })
    },
    initAlertFirstLaunch() {
      const isShowAlert = this.get_activatedBonusesCodesFromRegister
        .some(regCode => this.normalizedRegisterBonusesWithPromocodeCard
          .some(b => b.code === regCode && b.isPromocodeBonus))
      if (isShowAlert) this.showSuccessAlert()
    },
    initSlider() {
      this.isSliderInitiated = true
      let initialBonus = this.normalizedRegisterBonusesWithPromocodeCard[INITIAL_ACTIVE_SLIDE_INDEX]
      if (this.get_promocodeBonuses.length) {
        const promocodeBonus = this.normalizedRegisterBonusesWithPromocodeCard.find(bonus => bonus.isMaxMinDepositInPromocodes)
        if (promocodeBonus) initialBonus = promocodeBonus
      }
      this.selectedBonus = initialBonus
      this.$nextTick(() => {
        this.slideTo(this.selectedBonus?.index)
        this.SwiperInstance.on('activeIndexChange', this.onSlideChange)
      })
    },
    onNoDepBonusSuccess(bonus) {
      this.$refs.RegistrationBonusModalNoDepPromoCodeInfoRef.$open(bonus)
    },
    async onPromocodeSuccess() {
      this.showSuccessAlert()
      await this.$nextTick()
      const lastIndex = this.normalizedRegisterBonusesWithPromocodeCard.length - 3
      this.selectedBonus = this.normalizedRegisterBonusesWithPromocodeCard[lastIndex]
      this.SwiperInstance.activeIndex = this.normalizedRegisterBonusesWithPromocodeCard.length - 1
      this.$nextTick(() => {
        this.slideTo(lastIndex)
        this.SwiperInstance.pagination.destroy()
        this.SwiperInstance.pagination.init()
        this.SwiperInstance.pagination.update()
      })
    },
    onSlideChange() {
      const newActiveSlideIndex = this.SwiperInstance.activeIndex
      this.selectedBonus = this.normalizedRegisterBonusesWithPromocodeCard[newActiveSlideIndex]

      setTimeout(() => {
        if (this.selectedBonus.isPromocode && this.$refs.PromoCodeCardRef) {
          this.$refs.PromoCodeCardRef.$addFocus()
        }
      }, 400)
    },
    showSuccessAlert() {
      this.isShowAlertPromocodeActivated = true
      timeoutIdSuccessPromoCode = setTimeout(() => {
        this.isShowAlertPromocodeActivated = false
      }, SHOW_ALERT_MS)
    },
    slideTo(index) {
      this.SwiperInstance.slideTo(index, 400, false)
    },
    toCashboxBonus() {
      this.isSliderInitiated = false
      this.act_activeBonus(this.selectedBonus)
      this.$gtm.push({
        event: GTM_REG_BONUS.EVENT.ga_deposit_choose_deposit_successful,
        typeBonus: this.selectedBonus?.bonusType,
        minDeposit: this.selectedBonus.minDepositGrn,
      })

      this.transfer_depositBonus({ code: this.selectedBonus.code, bonusSum: this.selectedBonus?.minDepositGrn })
      this.$router.replace({
        path: this.$pathObj.cashboxDeposit.path,
        query: {},
      })
    },
    toCashboxNoBonus() {
      this.isSliderInitiated = false
      this.transfer_depositBonus({ code: BONUS_TYPE.NO_BONUS })
      this.$router.replace({
        path: this.$pathObj.cashboxDeposit.path,
        query: {},
      })
    },
  },
}
</script>
<style lang="scss" src="./style.scss" scoped></style>
